<template>
  <div v-if="!mainPageSlides.length" class="slider-skeleton"></div>
  <q-carousel
    v-else
    ref="carousel"
    v-model="selectSlide"
    class="main-slider"
    swipeable
    :infinite="infinite"
    animated
    :navigation="navigation"
    control-color="cas-secondary"
    :keep-alive="true"
    :autoplay="autoplay"
    @mouseenter="autoplay = false"
    @mouseleave="autoplay = true"
  >
    <main-slider-item
      v-for="(slide, index) in mainPageSlides"
      :key="slide.id"
      :name="index"
      :slide="slide"
      :first="index === 0"
      :image-list="imageList"
      :coefficients-data="coefficientsData[slide.eventId]"
    />

    <template #navigation-icon="{ active, onClick }">
      <button class="main-slider__nav" :class="{ '--active': active }" @click="onClick"></button>
    </template>
  </q-carousel>
</template>

<script>
import { mapGetters } from 'vuex'
import MainSliderItem from 'components/banners/MainSliderItem'
import getContentList from 'src/apollo/queries/getContentList.gql'
import getImageContentList from 'src/apollo/queries/getImageContentList.gql'
import getSportsbookPromoFeed from 'src/apollo/queries/getSportsbookPromoFeed.gql'

export default {
  name: 'MainSlider',
  components: { MainSliderItem },
  inject: ['mediaQueries'],
  props: {
    showNavigationOnMobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectSlide: 0,
      autoplay: true,
      mainPageSlides: [],
      imageList: {},
      coefficientsData: {},
    }
  },
  computed: {
    ...mapGetters(['getAuth']),
    isMobile() {
      return this.mediaQueries.mobileSmall
    },
    infinite() {
      if (!this.mainPageSlides) {
        return false
      }
      return this.mainPageSlides.length > 1
    },
    navigation() {
      return this.mainPageSlides.length > 1 && (!this.isMobile || this.showNavigationOnMobile)
    },
    markets() {
      return this.mainPageSlides
        ? this.mainPageSlides.map((event) => event.eventId).filter((id) => typeof id === 'string')
        : []
    },
  },
  beforeMount() {
    this.getSlides()
  },
  methods: {
    getMarket(marketIds) {
      const tournamentIds = marketIds.filter((id) => id)
      if (!tournamentIds.length) {
        return
      }
      this.$apollo
        .query({
          query: getSportsbookPromoFeed,
          variables: { tournamentIds },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          if (data?.sportsbookPromoFeed) {
            const sportData = JSON.parse(data.sportsbookPromoFeed)
            const filteredSportData = sportData.filter((el) => !!el)
            this.findMarket(filteredSportData)
          }
        })
    },
    findMarket(events) {
      let eventId
      events.forEach((event) => {
        event.forEach((option, index) => {
          if (index === 0) {
            eventId = option[index][0]
            this.coefficientsData[eventId] = []
          } else {
            this.coefficientsData[eventId].push({
              type: option[0][2],
              coefficients: [...Object.values(option[1][''])],
            })
          }
        })
      })
    },
    getSlides() {
      const variables = {
        type: 'banner',
      }
      this.$apollo
        .query({
          query: getContentList,
          variables,
        })
        .then(({ data }) => {
          const propsContentList = data?.propsContentList ?? []

          const props = propsContentList
            .map((slide) => ({ id: slide.id, ...JSON.parse(slide.props) }))
            .filter((slide) => slide.active)
            .sort((a, b) => a.order - b.order)

          const listId = props.reduce((acc = [], current) => {
            function getId(elem) {
              if (elem?.id) {
                return elem.id
              } else if (typeof elem === 'number') {
                return elem
              } else {
                return null
              }
            }

            const array = [getId(current.image), getId(current.imageBackground), getId(current.imageTop)]

            acc.push(...array)
            return acc
          }, [])

          const uniqueListId = Array.from(new Set(listId)).filter((i) => i !== null)

          this.$apollo
            .query({
              query: getImageContentList,
              variables: {
                id: uniqueListId,
              },
              fetchPolicy: 'network-only',
            })
            .then(({ data }) => {
              if (data?.imageContentList) {
                const newObject = {}
                data.imageContentList.forEach((i) => {
                  newObject[i.id] = i
                })

                this.imageList = newObject
              }
            })

          this.mainPageSlides = props
          this.getMarket(this.markets)
        })
    },
  },
}
</script>

<style lang="scss">
.slider-skeleton {
  margin-top: 12px;
  margin-bottom: 40px;
  border-radius: var(--radius-2);
  height: 180px;
  background: var(--cas-main-slider-skeleton);

  @media (min-width: 768px) {
    height: 275px;
  }

  @media (min-width: 1100px) {
    margin-top: 24px;
  }
}

.main-slider {
  margin-top: 12px;
  margin-bottom: 40px;
  background: rgba(19, 31, 96, 0.4);
  border-radius: var(--radius-2);
  height: auto;
  display: flex;
  flex-direction: column;
  overflow: visible;
  @media (min-width: 1100px) {
    margin-top: 24px;
  }

  &__nav {
    background: none;
    padding: 6px;
    border: none;
    cursor: pointer;
    line-height: 0;

    &:after {
      content: '';
      border-radius: 50%;
      width: 8px;
      height: 8px;
      border: 1px solid var(--cas-light-500);
      display: inline-block;
      margin: 0;
    }

    &.--active {
      &:after {
        border-color: var(--cas-secondary);
        background: var(--cas-secondary);
        box-shadow: 0 0 0 2px rgba(#f7b636, 0.35);
      }
    }
  }

  .q-carousel__navigation {
    position: absolute;
    bottom: -32px;
  }
  .q-carousel__navigation-inner {
    display: flex;
    justify-content: center;
    margin: 0;
  }
}

@media (max-width: 575px) {
  .main-slider {
    margin-bottom: 0;
  }
}
</style>
